/**
 * Durak (Podkidnoy) — server-authoritative rules engine (Russian shedding game, 4 players, every player for themselves).
 *
 * Rules implemented:
 *  - A 36-card deck (6 to ace), six cards each. The last card of the stock is turned face up and names the trump suit;
 *    it is drawn last. The player holding the lowest trump attacks first.
 *  - A bout: the attacker leads one card and the defender (the player to the attacker's left) must beat it with a
 *    higher card of the same suit or with any trump (a higher trump over a trump), or take the cards. Once a card is
 *    beaten, any other player may throw in a card of a rank already on the table (the attacker first, then clockwise).
 *  - A bout holds at most six attacking cards and never more than the defender's hand held when it began.
 *  - If every card is beaten the table is discarded and the defender attacks next. If the defender takes, attackers may
 *    pile on more matching cards first; the defender picks up everything and the player after them attacks.
 *  - After every bout the players refill to six from the stock — the attacker first, then clockwise, the defender last.
 *  - A player with no cards once the stock is gone is out. The last player holding cards is the Durak (the fool) and
 *    finishes last. A hard cap on bouts ends a very long game, ranking the players left by how few cards they hold.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface DurakConfig {
  /** Hard cap on bouts so a game always terminates. */
  maxBouts: number;
  boutEndDelayMs: number;
}

export type DurakPhase = 'ATTACK' | 'DEFEND' | 'BOUT_END' | 'MATCH_END';

export interface DurakPair {
  attack: CardId;
  defense: CardId | null;
}

export interface DurakBout {
  pairs: DurakPair[];
  outcome: 'defended' | 'taken';
  attacker: number;
  defender: number;
}

export interface DurakState {
  config: DurakConfig;
  players: string[];
  phase: DurakPhase;
  hands: CardId[][];
  /** Undrawn cards, next draw first; the last card is the trump card. */
  stock: CardId[];
  trump: Suit;
  /** The trump card as dealt (kept after it has been drawn so the suit stays known). */
  trumpCard: CardId;
  table: DurakPair[];
  attacker: number;
  defender: number;
  /** Seats still to be offered the chance to attack / throw in, in order (the first one is up). */
  queue: number[];
  /** The defender has given up and the attackers are adding cards they will have to pick up. */
  taking: boolean;
  /** Most attacking cards this bout may hold. */
  limit: number;
  boutNo: number;
  /** Seats in the order they went out. */
  out: number[];
  discarded: CardId[];
  lastBout: DurakBout | null;
  /** Set once the game has ended: the seat left holding cards (null if everybody got out). */
  durak: number | null;
}

export type DurakAction =
  | { type: 'attack'; card: CardId }
  | { type: 'defend'; card: CardId }
  | { type: 'take' }
  | { type: 'pass' };

export interface DurakLegal {
  /** Cards that may be led or thrown in now. */
  attack: CardId[];
  /** Cards that beat the card being attacked. */
  defend: CardId[];
  canTake: boolean;
  canPass: boolean;
}

export interface DurakView {
  gameType: 'DURAK';
  config: DurakConfig;
  seat: number | null;
  phase: DurakPhase;
  hand: CardId[];
  handCounts: number[];
  trump: Suit;
  /** The face-up trump card at the bottom of the stock; null once it has been drawn. */
  trumpCard: CardId | null;
  stockCount: number;
  table: DurakPair[];
  attacker: number;
  defender: number;
  turn: number;
  taking: boolean;
  limit: number;
  boutNo: number;
  out: number[];
  /** Cards that have left the game (public, so counting is possible). */
  discarded: CardId[];
  lastBout: DurakBout | null;
  durak: number | null;
  /** Cards each seat holds (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: DurakConfig = {
  maxBouts: 300,
  boutEndDelayMs: 1400,
};

const SEATS = 4;
const HAND = 6;
const MAX_ATTACKS = 6;
const rank = (c: CardId) => parseCard(c).rank;
const suit = (c: CardId) => parseCard(c).suit;

/** True when `defense` beats `attack` given the trump suit. */
export function durakBeats(defense: CardId, attack: CardId, trump: Suit): boolean {
  const d = parseCard(defense);
  const a = parseCard(attack);
  if (d.suit === a.suit) return d.rank > a.rank;
  return d.suit === trump;
}

/** The 36-card Durak deck. */
export const durakDeck = (): CardId[] => buildDeck().filter((c) => rank(c) >= 6);

function tableRanks(table: readonly DurakPair[]): Set<number> {
  const ranks = new Set<number>();
  for (const p of table) {
    ranks.add(rank(p.attack));
    if (p.defense) ranks.add(rank(p.defense));
  }
  return ranks;
}

/** Cards `hand` may lead (empty table) or throw in (a rank already on the table, room for one more). */
export function durakAttackCards(hand: readonly CardId[], table: readonly DurakPair[], limit: number): CardId[] {
  if (table.length === 0) return [...hand];
  if (table.length >= limit) return [];
  const ranks = tableRanks(table);
  return hand.filter((c) => ranks.has(rank(c)));
}

/** Cards that beat the card being attacked. */
export function durakDefendCards(hand: readonly CardId[], attack: CardId, trump: Suit): CardId[] {
  return hand.filter((c) => durakBeats(c, attack, trump));
}

const undefended = (table: readonly DurakPair[]): DurakPair | undefined => table.find((p) => p.defense === null);

/** Seats with cards, clockwise from `from` (inclusive). */
function activeFrom(hands: readonly (readonly CardId[])[], from: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < SEATS; i++) {
    const seat = (from + i) % SEATS;
    if (hands[seat]!.length > 0) out.push(seat);
  }
  return out;
}

/** Attackers who still have a card they may add, the primary attacker first. */
function buildQueue(state: DurakState): number[] {
  if (state.table.length >= state.limit) return [];
  const out: number[] = [];
  for (let i = 0; i < SEATS; i++) {
    const seat = (state.attacker + i) % SEATS;
    if (seat === state.defender) continue;
    if (durakAttackCards(state.hands[seat]!, state.table, state.limit).length > 0) out.push(seat);
  }
  return out;
}

function startBout(state: DurakState, attacker: number): void {
  state.attacker = attacker;
  state.defender = activeFrom(state.hands, attacker + 1).find((s) => s !== attacker) ?? (attacker + 1) % SEATS;
  state.table = [];
  state.taking = false;
  state.limit = Math.min(MAX_ATTACKS, state.hands[state.defender]!.length);
  state.queue = [attacker];
  state.phase = 'ATTACK';
  state.boutNo++;
}

/** Refill from the stock: attacker first, then clockwise, the defender last. */
function refill(state: DurakState, events: EngineEvent[]): number[] {
  const order: number[] = [];
  for (let i = 0; i < SEATS; i++) {
    const seat = (state.attacker + i) % SEATS;
    if (seat !== state.defender) order.push(seat);
  }
  order.push(state.defender);
  for (const seat of order) {
    const hand = state.hands[seat]!;
    let drawn = 0;
    while (hand.length < HAND && state.stock.length > 0) {
      hand.push(state.stock.shift()!);
      drawn++;
    }
    if (drawn > 0) {
      state.hands[seat] = sortHand(hand);
      events.push({ type: 'draw', seat, count: drawn });
    }
  }
  return order;
}

function endBout(state: DurakState, outcome: 'defended' | 'taken', events: EngineEvent[]): void {
  const pairs = state.table.map((p) => ({ ...p }));
  const cards = pairs.flatMap((p) => (p.defense ? [p.attack, p.defense] : [p.attack]));
  if (outcome === 'taken') {
    state.hands[state.defender] = sortHand([...state.hands[state.defender]!, ...cards]);
    events.push({ type: 'take', seat: state.defender, count: cards.length });
  } else {
    state.discarded.push(...cards);
    events.push({ type: 'discard', count: cards.length });
  }
  state.lastBout = { pairs, outcome, attacker: state.attacker, defender: state.defender };
  state.table = [];
  state.queue = [];
  state.taking = false;
  const order = refill(state, events);
  if (state.stock.length === 0) {
    for (const seat of order) {
      if (state.hands[seat]!.length === 0 && !state.out.includes(seat)) {
        state.out.push(seat);
        events.push({ type: 'out', seat, place: state.out.length });
      }
    }
  }
  state.phase = 'BOUT_END';
}

function finish(state: DurakState, events: EngineEvent[]): void {
  state.phase = 'MATCH_END';
  const left = activeFrom(state.hands, 0);
  state.durak = left.length === 1 ? left[0]! : null;
  events.push({ type: 'matchEnd', standings: standings(state), durak: state.durak });
}

export const durakEngine: GameEngine<DurakConfig, DurakState, DurakAction, DurakView, DurakLegal> = {
  gameType: 'DURAK',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxBouts', c.maxBouts, 20, 2000);
    checkInt('boutEndDelayMs', c.boutEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Durak needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const deck = shuffle(durakDeck(), rng);
    const hands: CardId[][] = [[], [], [], []];
    for (let i = 0; i < HAND * SEATS; i++) hands[i % SEATS]!.push(deck[i]!);
    const stock = deck.slice(HAND * SEATS);
    const trumpCard = stock[stock.length - 1]!;
    const trump = suit(trumpCard);
    // The lowest trump in any hand attacks first; with none, seat 0.
    let first = 0;
    let lowest = 99;
    hands.forEach((h, s) => {
      for (const c of h) if (suit(c) === trump && rank(c) < lowest) { lowest = rank(c); first = s; }
    });
    const state: DurakState = {
      config,
      players,
      phase: 'ATTACK',
      hands: hands.map((h) => sortHand(h)),
      stock,
      trump,
      trumpCard,
      table: [],
      attacker: first,
      defender: (first + 1) % SEATS,
      queue: [],
      taking: false,
      limit: HAND,
      boutNo: 0,
      out: [],
      discarded: [],
      lastBout: null,
      durak: null,
    };
    startBout(state, first);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if ((raw.type === 'attack' || raw.type === 'defend') && typeof raw.card === 'string' && raw.card.length <= 4) {
      return { type: raw.type, card: raw.card };
    }
    if (raw.type === 'take') return { type: 'take' };
    if (raw.type === 'pass') return { type: 'pass' };
    throw new EngineError('INVALID_ACTION', 'Unknown Durak action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'The game is over');
    assertEngine(durakEngine.getActors(state).includes(seat), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;

    if (state.phase === 'ATTACK') {
      if (action.type === 'pass') {
        assertEngine(state.table.length > 0, 'ILLEGAL_MOVE', 'You must lead a card');
        state.queue.shift();
        events.push({ type: 'pass', seat });
        if (state.queue.length === 0) endBout(state, state.taking ? 'taken' : 'defended', events);
        return events;
      }
      assertEngine(action.type === 'attack', 'ILLEGAL_MOVE', 'Attack with a card or pass');
      assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
      assertEngine(durakAttackCards(hand, state.table, state.limit).includes(action.card), 'ILLEGAL_MOVE', 'That card cannot be played now');
      removeCards(hand, [action.card]);
      state.table.push({ attack: action.card, defense: null });
      events.push({ type: 'cardPlayed', seat, card: action.card, role: 'attack' });
      if (state.taking) {
        state.queue = buildQueue(state);
        if (state.queue.length === 0) endBout(state, 'taken', events);
      } else {
        state.queue = [];
        state.phase = 'DEFEND';
      }
      return events;
    }

    if (state.phase === 'DEFEND') {
      const open = undefended(state.table)!;
      if (action.type === 'take') {
        state.taking = true;
        state.phase = 'ATTACK';
        state.queue = buildQueue(state);
        events.push({ type: 'takeStarted', seat });
        if (state.queue.length === 0) endBout(state, 'taken', events);
        return events;
      }
      assertEngine(action.type === 'defend', 'ILLEGAL_MOVE', 'Beat the card or take');
      assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
      assertEngine(durakBeats(action.card, open.attack, state.trump), 'ILLEGAL_MOVE', 'That card does not beat the attack');
      removeCards(hand, [action.card]);
      open.defense = action.card;
      events.push({ type: 'cardPlayed', seat, card: action.card, role: 'defense' });
      if (hand.length === 0 || state.table.length >= state.limit) {
        endBout(state, 'defended', events);
        return events;
      }
      state.queue = buildQueue(state);
      state.phase = 'ATTACK';
      if (state.queue.length === 0) endBout(state, 'defended', events);
      return events;
    }

    throw new EngineError('NOT_YOUR_TURN', 'Not your turn');
  },

  getActors(state) {
    if (state.phase === 'ATTACK') return state.queue.length > 0 ? [state.queue[0]!] : [];
    if (state.phase === 'DEFEND') return [state.defender];
    return [];
  },

  getLegalActions(state, seat) {
    const none: DurakLegal = { attack: [], defend: [], canTake: false, canPass: false };
    if (!durakEngine.getActors(state).includes(seat)) return none;
    const hand = state.hands[seat]!;
    if (state.phase === 'ATTACK') {
      return { attack: durakAttackCards(hand, state.table, state.limit), defend: [], canTake: false, canPass: state.table.length > 0 };
    }
    const open = undefended(state.table);
    return { attack: [], defend: open ? durakDefendCards(hand, open.attack, state.trump) : [], canTake: true, canPass: false };
  },

  getTimeoutAction(state, seat) {
    const legal = durakEngine.getLegalActions(state, seat);
    if (state.phase === 'DEFEND') return { type: 'take' };
    if (state.table.length > 0) return { type: 'pass' };
    const lowest = [...legal.attack].sort((a, b) => Number(suit(a) === state.trump) - Number(suit(b) === state.trump) || rank(a) - rank(b))[0]!;
    return { type: 'attack', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'DURAK',
      config: state.config,
      seat,
      phase: state.phase,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      trump: state.trump,
      trumpCard: state.stock.length > 0 ? state.trumpCard : null,
      stockCount: state.stock.length,
      table: (state.phase === 'BOUT_END' ? (state.lastBout?.pairs ?? []) : state.table).map((p) => ({ ...p })),
      attacker: state.attacker,
      defender: state.defender,
      turn: state.phase === 'DEFEND' ? state.defender : (state.queue[0] ?? state.attacker),
      taking: state.taking,
      limit: state.limit,
      boutNo: state.boutNo,
      out: [...state.out],
      discarded: [...state.discarded],
      lastBout: state.lastBout ? { ...state.lastBout, pairs: state.lastBout.pairs.map((p) => ({ ...p })) } : null,
      durak: state.durak,
      totals: state.hands.map((h) => h.length),
    };
  },

  getSystemStep(state) {
    return state.phase === 'BOUT_END' ? { kind: 'collectTrick', delayMs: state.config.boutEndDelayMs } : null;
  },

  runSystemStep(state) {
    const events: EngineEvent[] = [];
    if (state.phase !== 'BOUT_END') return events;
    const left = activeFrom(state.hands, 0);
    if (left.length <= 1 || state.boutNo >= state.config.maxBouts) {
      finish(state, events);
      return events;
    }
    const bout = state.lastBout!;
    // A defender who beat everything attacks next; one who took is skipped. Anyone out is skipped too.
    const from = bout.outcome === 'defended' ? bout.defender : bout.defender + 1;
    startBout(state, activeFrom(state.hands, from)[0]!);
    events.push({ type: 'boutStarted', attacker: state.attacker, defender: state.defender });
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.boutNo,
};

/** Places: players in the order they went out, then whoever is left by fewest cards (the Durak last). */
function standings(state: DurakState): Standing[] {
  const remaining = [0, 1, 2, 3].filter((s) => !state.out.includes(s)).sort((a, b) => state.hands[a]!.length - state.hands[b]!.length);
  const ranked = [...state.out, ...remaining];
  return ranked.map((seat, i) => ({ seat, place: i + 1, score: 100 - i * 10 - state.hands[seat]!.length }));
}
